import Papa from "papaparse";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaChalkboard } from "react-icons/fa";
import { GiProcessor } from "react-icons/gi";
import {
  HiOutlineDownload,
  HiOutlineLibrary,
  HiOutlineUpload,
  HiOutlineUsers,
} from "react-icons/hi";
import {
  HiOutlineBookOpen,
  HiOutlineDocumentText,
  HiOutlineEnvelope,
  HiOutlineExclamationTriangle,
  HiOutlineFolderOpen,
} from "react-icons/hi2";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Badge from "../../components/Badge";
import Button from "../../components/Button";
import Card from "../../components/Card";
import LoadingSpinner from "../../components/LoadingSpinner";
import PageHeader from "../../components/PageHeader";
import StatCard from "../../components/StatCard";
import Table from "../../components/Table";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../services/api";
import { getStatusVariant } from "../../utils/styleUtils";

const IMPORT_TYPES = { USERS: "users", SUBJECTS: "subjects", THEMES: "themes" };
const EXPORT_CONFIG = [
  {
    key: "users",
    title: "Export Users",
    description: "Download all user data as CSV",
  },
  {
    key: "subjects",
    title: "Export Subjects",
    description: "Download all subjects as CSV",
  },
  {
    key: "sessions",
    title: "Export Sessions",
    description: "Download session history",
  },
  {
    key: "bookings",
    title: "Export Bookings",
    description: "Download booking records",
  },
  {
    key: "themes",
    title: "Export Themes",
    description: "Download all themes as CSV",
  },
];
const QUICK_LINKS = [
  { to: "/admin/users", label: "Users", icon: HiOutlineUsers },
  { to: "/admin/subjects", label: "Subjects", icon: HiOutlineBookOpen },
  {
    to: "/admin/penalties",
    label: "Penalties",
    icon: HiOutlineExclamationTriangle,
  },
  { to: "/admin/emails", label: "Emails", icon: HiOutlineEnvelope },
  { to: "/admin/system", label: "System", icon: GiProcessor },
  { to: "/admin/audit-logs", label: "Audit Logs", icon: HiOutlineDocumentText },
  { to: "/admin/theme", label: "Themes", icon: HiOutlineFolderOpen },
];
const IMPORT_CONFIG = [
  {
    key: IMPORT_TYPES.USERS,
    title: "Import Users",
    description: "Upload CSV with user data",
  },
  {
    key: IMPORT_TYPES.SUBJECTS,
    title: "Import Subjects",
    description: "Upload CSV with subjects",
  },
  {
    key: IMPORT_TYPES.THEMES,
    title: "Import Themes",
    description: "Upload CSV with theme data",
  },
];

const HEADER_MAP = new Map([
  ["ID", "id"],
  ["First Name", "firstName"],
  ["Last Name", "lastName"],
  ["Email", "email"],
  ["Role", "role"],
  ["Phone", "phone"],
  ["CID", "cid"],
  ["Bio", "bio"],
  ["Avatar URL", "avatarUrl"],
  ["Subjects", "subjects"],
  ["Availability", "availability"],
  ["Rating", "rating"],
  ["Total Ratings", "totalRatings"],
  ["Certificates", "certificates"],
  ["Timezone", "timezone"],
  ["Last Login", "lastLogin"],
  ["Banned", "banned"],
  ["Banned At", "bannedAt"],
  ["Unbanned At", "unbannedAt"],
  ["Verified", "verified"],
  ["Created At", "createdAt"],
  ["Updated At", "updatedAt"],
  ["Code", "code"],
  ["Name", "name"],
  ["Description", "description"],
  ["Tags", "tags"],
  ["Enabled", "isEnabled"],
  ["Primary", "colors.primary"],
  ["Secondary", "colors.secondary"],
  ["Accent", "colors.accent"],
  ["Background", "colors.background"],
  ["Surface", "colors.surface"],
  ["Border", "colors.border"],
  ["Text", "colors.text"],
]);

// Utilities (same as before - shortened)
const getErrorMessage = (error, fallback) =>
  error?.response?.data?.error || error?.message || fallback;
const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob),
    link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
const toBoolean = (v) => ["true", "1", "yes"].includes(String(v).toLowerCase());
const setNestedValue = (obj, path, value) => {
  const keys = path.split(".");
  let current = obj;
  keys.forEach((key, i) =>
    i === keys.length - 1
      ? (current[key] = value)
      : ((current[key] ??= {}), (current = current[key]))
  );
};
const parseCSV = (text, type) => {
  const warnings = [],
    result = Papa.parse(text, { header: true, skipEmptyLines: true });
  result.errors?.forEach((e) => warnings.push(`Row ${e.row}: ${e.message}`));
  const data = result.data.map((row) => {
    const obj = {};
    Object.entries(row).forEach(([header, value]) => {
      if (!value) return;
      const mappedKey = HEADER_MAP.get(header);
      if (!mappedKey) return warnings.push(`Unknown column ignored: ${header}`);
      mappedKey.includes(".")
        ? setNestedValue(obj, mappedKey, value)
        : (obj[mappedKey] = value);
    });
    if (type === "themes") {
      obj.isEnabled = toBoolean(obj.isEnabled);
      obj.colors ??= {};
      Object.keys(obj.colors).forEach(
        (k) => (obj.colors[k] = String(obj.colors[k]).trim())
      );
    }
    return obj;
  });
  return { data, warnings };
};

const ExportRow = ({ title, description, onExport }) => (
  <div className="flex items-center justify-between p-3 bg-background rounded-lg border border-border/60">
    <div>
      <p className="font-medium text-text">{title}</p>
      <p className="text-xs text-text/70">{description}</p>
    </div>
    <Button size="sm" onClick={onExport}>
      Export
    </Button>
  </div>
);

const ImportRow = ({ title, description, onGuide, onImport, disabled }) => (
  <div className="flex items-center justify-between p-3 bg-background rounded-lg border border-border/60">
    <div>
      <p className="font-medium text-text">{title}</p>
      <p className="text-xs text-text/70">{description}</p>
    </div>
    <div className="flex gap-2">
      <Button size="sm" variant="outline" onClick={onGuide}>
        Guide
      </Button>
      <Button
        size="sm"
        variant="secondary"
        onClick={onImport}
        disabled={disabled}
      >
        Import
      </Button>
    </div>
  </div>
);

export default function AdminDashboard() {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importLoading, setImportLoading] = useState(false);
  const [importType, setImportType] = useState(null);

  // Same handlers (shortened)
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [statsRes, bookingsRes, sessionsRes] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/bookings?limit=10"),
          api.get("/admin/sessions?limit=10"),
        ]);
        if (active) {
          setStats(statsRes.stats);
          setBookings(bookingsRes.bookings ?? []);
          setSessions(sessionsRes.sessions ?? []);
        }
      } catch (error) {
        toast.error(getErrorMessage(error, "Failed to load dashboard"));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleExport = useCallback(async (type) => {
    try {
      const res = await api.get(`/import-export/export/${type}`, {
        responseType: "blob",
      });
      downloadBlob(
        res,
        `${type}_${new Date().toISOString().split("T")[0]}.csv`
      );
      toast.success(`${type} exported successfully`);
    } catch (error) {
      toast.error(getErrorMessage(error, `Failed to export ${type}`));
    }
  }, []);

  const downloadImportGuide = useCallback(async (type) => {
    try {
      const res = await api.get(`/import-export/guide/${type}`, {
        responseType: "blob",
      });
      downloadBlob(res, `${type}_import_guide.txt`);
      toast.success("Guide downloaded");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to download guide"));
    }
  }, []);

  const handleImportClick = useCallback((type) => {
    setImportType(type);
    fileInputRef.current?.click();
  }, []);

  const refreshDashboard = useCallback(async () => {
    const [statsRes, bookingsRes, sessionsRes] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/bookings?limit=10"),
      api.get("/admin/sessions?limit=10"),
    ]);
    setStats(statsRes.stats);
    setBookings(bookingsRes.bookings ?? []);
    setSessions(sessionsRes.sessions ?? []);
  }, []);

  const handleFileSelect = useCallback(
    async (e) => {
      if (!importType || !e.target.files?.[0]) return;
      setImportLoading(true);
      try {
        const text = await e.target.files[0].text();
        const { data, warnings } = parseCSV(text, importType);
        warnings.forEach((w) => toast.warn(w));
        const { results } = await api.post(
          `/import-export/import/${importType}`,
          { [importType]: data }
        );
        toast.success(
          `Import complete: ${results.created} created, ${results.updated} updated, ${results.failed} failed`
        );
        await refreshDashboard();
      } catch (error) {
        toast.error(getErrorMessage(error, "Import failed"));
      } finally {
        setImportLoading(false);
        e.target.value = "";
        setImportType(null);
      }
    },
    [importType, refreshDashboard]
  );

  // SINGLE TABLE COLUMN FACTORY
  const createTableColumns = (type, linkPrefix) =>
    useMemo(
      () => [
        {
          key: "subject",
          label: "Subject",
          render: (item) => (
            <Link
              to={`/${linkPrefix}/${item._id}`}
              className="text-primary font-medium hover:opacity-80"
            >
              {item.subjectId?.name}
            </Link>
          ),
        },
      ],
      []
    );

  const bookingColumns = createTableColumns("bookings", "bookings");
  const sessionColumns = createTableColumns("sessions", "sessions");

  // SINGLE STATCARD FACTORY
  const StatCardRow = ({ stats }) => (
    <StatCard
      title={stats.title}
      value={stats.value}
      icon={stats.icon}
      subtitle={stats.subtitle}
      footer={stats.footer}
      gradient={stats.gradient}
    />
  );

  const STAT_CARDS = [
    {
      title: "Total Bookings",
      value: stats?.bookings?.total,
      icon: <HiOutlineLibrary className="w-8 h-8" />,
      subtitle: `${stats?.bookings?.successRate}% success rate`,
      footer: `${stats?.bookings?.recent} in last 7 days`,
      gradient:
        "linear-gradient(135deg, var(--color-primary), var(--color-secondary))",
    },
    {
      title: "Total Sessions",
      value: stats?.sessions?.total,
      icon: <FaChalkboard className="w-8 h-8" />,
      subtitle: `${stats?.sessions?.completionRate}% completion rate`,
      footer: `${stats?.sessions?.recent} in last 7 days`,
      gradient:
        "linear-gradient(135deg, var(--color-secondary), var(--color-accent))",
    },
    {
      title: "Total Users",
      value: stats?.users?.total,
      icon: <HiOutlineUsers className="w-8 h-8" />,
      subtitle: `${stats?.users?.students}S · ${stats?.users?.tutors}T · ${stats?.users?.admins}A`,
      footer: `${stats?.users?.verified} verified · ${stats?.users?.unverified} pending`,
      gradient:
        "linear-gradient(135deg, var(--color-accent), var(--color-primary))",
    },
    {
      title: "Active Penalties",
      value: stats?.penalties?.active,
      icon: <HiOutlineExclamationTriangle className="w-8 h-8" />,
      subtitle: "Requires attention",
      gradient:
        "linear-gradient(135deg, var(--color-primary), var(--color-primary))",
    },
  ];

  if (loading) return <LoadingSpinner size="lg" className="py-16" />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <PageHeader
        title="Admin Control Panel"
        subtitle={`Welcome back, ${user?.name?.first}.`}
      />

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {STAT_CARDS.map((card, i) => (
            <StatCardRow key={i} stats={card} />
          ))}
        </div>
      )}

      <Card className="mb-8">
        <h2 className="text-lg font-semibold text-text mb-4">
          Quick Management
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {QUICK_LINKS.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className="flex flex-col items-center p-4 rounded-lg hover:bg-background transition-colors border border-border/60"
            >
              <Icon className="w-8 h-8 text-primary mb-2" />
              <span className="text-sm font-medium text-text text-center">
                {label}
              </span>
            </Link>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card>
          <h2 className="text-lg font-semibold text-text mb-4 flex items-center">
            <HiOutlineUpload className="w-5 h-5 mr-2 text-primary" />
            Export Data
          </h2>
          <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
            {EXPORT_CONFIG.map(({ key, title, description }) => (
              <ExportRow
                key={key}
                title={title}
                description={description}
                onExport={() => handleExport(key)}
              />
            ))}
          </div>
        </Card>
        <Card>
          <h2 className="text-lg font-semibold text-text mb-4 flex items-center">
            <HiOutlineDownload className="w-5 h-5 mr-2 text-primary" />
            Import Data
          </h2>
          <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
            {IMPORT_CONFIG.map(({ key, title, description }) => (
              <ImportRow
                key={key}
                title={title}
                description={description}
                onGuide={() => downloadImportGuide(key)}
                onImport={() => handleImportClick(key)}
                disabled={importLoading}
              />
            ))}
            <div className="p-4 bg-accent/10 border border-accent/40 rounded-lg">
              <p className="text-sm text-text">
                <strong>Note:</strong> CSV files should have headers matching
                the export format.
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h2 className="text-xl font-semibold mb-4 text-text">
            Recent Bookings
          </h2>
          <Table columns={bookingColumns} data={bookings} />
        </Card>
        <Card>
          <h2 className="text-xl font-semibold mb-4 text-text">
            Recent Sessions
          </h2>
          <Table columns={sessionColumns} data={sessions} />
        </Card>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        className="hidden"
        onChange={handleFileSelect}
        disabled={importLoading}
      />
    </div>
  );
}
